import {
	dispatchLensEvent,
	LENS_OPEN_TARGET_EVENT,
	LENS_OPEN_TARGET_RESPONSE_EVENT,
	parseLensEventDetail,
} from "./lens-context";

function requestOwlettoLensTarget(
	detail: Record<string, unknown>,
): Promise<boolean> {
	const id = `open-browser-target:${crypto.randomUUID()}`;
	return new Promise<boolean>((resolve) => {
		const timeout = window.setTimeout(() => {
			document.removeEventListener(LENS_OPEN_TARGET_RESPONSE_EVENT, onResponse);
			resolve(false);
		}, 1_500);
		const onResponse = (event: Event) => {
			const response = parseLensEventDetail<{ id: string; ok: boolean }>(event);
			if (response?.id !== id) return;
			window.clearTimeout(timeout);
			document.removeEventListener(LENS_OPEN_TARGET_RESPONSE_EVENT, onResponse);
			resolve(response.ok === true);
		};
		document.addEventListener(LENS_OPEN_TARGET_RESPONSE_EVENT, onResponse);
		dispatchLensEvent(LENS_OPEN_TARGET_EVENT, { id, ...detail });
	});
}

/**
 * Open a browser handoff in a fresh tab for page-visit activation.
 *
 * `prepare` is used when an expired draft must be recreated first. In the
 * top-level app we reserve the tab synchronously (preserving the click's popup
 * permission), run the durable preparation, then navigate it. A failed prepare
 * closes only that still-blank tab and propagates the actionable error.
 */
export async function openBrowserHandoffTab(
	url: string,
	prepare?: () => Promise<unknown>,
): Promise<boolean> {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return false;
	}
	if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
	if (document.documentElement.hasAttribute("data-owletto-lens-bridge")) {
		if (prepare) await prepare();
		return requestOwlettoLensTarget({ url: parsed.toString() });
	}
	if (window.parent === window) {
		// The `noopener` window feature forces a null return even on success. Open a
		// same-origin blank first so popup blocking remains observable, sever its
		// opener before external content loads, then navigate it to the handoff URL.
		const opened = window.open("", "_blank");
		if (!opened) return false;
		opened.opener = null;
		try {
			if (prepare) await prepare();
			opened.location.replace(parsed.toString());
		} catch (error) {
			opened.close();
			throw error;
		}
		return true;
	}
	if (prepare) await prepare();

	const id = `open-tab:${crypto.randomUUID()}`;
	return new Promise<boolean>((resolve) => {
		const timeout = window.setTimeout(() => {
			window.removeEventListener("message", onMessage);
			resolve(false);
		}, 1_500);
		const onMessage = (event: MessageEvent) => {
			if (event.data?.id !== id) return;
			window.clearTimeout(timeout);
			window.removeEventListener("message", onMessage);
			resolve(event.data?.ok === true);
		};
		window.addEventListener("message", onMessage);
		window.parent.postMessage(
			{ id, op: "openTab", params: { url: parsed.toString() } },
			"*",
		);
	});
}

/** Bring the Lobu app tab that owns Lens to the foreground without reloading it. */
export async function focusLobuHostTab(): Promise<boolean> {
	if (document.documentElement.hasAttribute("data-owletto-lens-bridge")) {
		return requestOwlettoLensTarget({ focusHost: true });
	}
	window.focus();
	return true;
}
